/**
 * @file downloadReportExport.ts
 * @brief Blob download helper for server-side report CSV/PDF exports.
 *
 * @details
 * Phase C — avoids browser-only CSV for large result sets. Related:
 * BACKEND reports export routes, features/admin-invoices blob download pattern.
 *
 * @author DES
 * @date 27-07-2026
 */

import { apiClient } from "@/shared/api/client";
import { adminEndpoints, companyEndpoints } from "@/shared/api/endpoints";

export type ReportExportScope = "admin" | "company";
export type ReportExportFormat = "csv" | "pdf";

export type ServerReportExport = {
  scope: ReportExportScope;
  reportKey: string;
  params?: Record<string, string | number | undefined | null>;
};

function buildUrl(
  scope: ReportExportScope,
  reportKey: string,
  format: ReportExportFormat,
  params?: ServerReportExport["params"],
): string {
  const base =
    scope === "admin"
      ? adminEndpoints.reportsExport(reportKey, format)
      : companyEndpoints.reportsExport(reportKey, format);
  const usp = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== "") usp.set(k, String(v));
    }
  }
  // (24-08-2026) Same calendar as the chart the export was taken from —
  // otherwise a downloaded CSV buckets on UTC days while the page shows local.
  try {
    usp.set("tz", Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  } catch {
    usp.set("tz", "UTC");
  }
  const q = usp.toString();
  return q ? `${base}?${q}` : base;
}

function filenameFromDisposition(
  header: string | undefined,
  fallback: string,
): string {
  if (!header) return fallback;
  const match = /filename\*?=(?:UTF-8''|")?([^\";]+)"?/i.exec(header);
  if (!match?.[1]) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export async function downloadReportExport(
  config: ServerReportExport,
  format: ReportExportFormat,
): Promise<void> {
  const url = buildUrl(config.scope, config.reportKey, format, config.params);
  const res = await apiClient.get(url, { responseType: "blob" });
  const fallback = `verblizr-${config.reportKey}.${format}`;
  const name = filenameFromDisposition(
    res.headers?.["content-disposition"] as string | undefined,
    fallback,
  );
  const blobUrl = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}
